import { isMatch } from "lodash";
import { Fragment, Mark, MarkType, Node as ProseNode, ResolvedPos, Slice } from "prosemirror-model";
import { EditorState, Selection } from "prosemirror-state";
import { Transform } from "prosemirror-transform";
import { Decoration, type DecorationAttrs, EditorView } from "prosemirror-view";

import { promptEnabled } from "~/lib/prose/plugins/prompt";

import { PLAIN_TEXT_NODE_TYPE_NAMES } from "./nodes/plaintext";

export const getText = (block: ProseNode, leafText = "\n") => {
	if (block.isText) {
		return block.text || "";
	}
	if (block.isLeaf && !block.isText) {
		return leafText;
	}
	return block.textBetween(0, block.content.size, "", leafText);
};

/**
 * Determine whether a block looks empty. This means that there is no text
 * content, and there doesn't appear to be anything on the line. An example of
 * _not_ being visually empty is a talking point, which has a checkbox on the
 * left.
 */
export function isVisuallyEmpty(node: ProseNode, { ignoreText }: { ignoreText?: boolean } = {}): boolean {
	if (!ignoreText && getText(node)) return false;
	if (["todo", "agenda", "goal"].includes(node.type.name)) return false;
	if (PLAIN_TEXT_NODE_TYPE_NAMES.includes(node.type.name) && node.attrs.indent > 0) return false;
	if (promptEnabled(node)) return false;
	return true;
}

export const isSelectionFromStart = (selection: Selection) => {
	const {
		$from: { nodeBefore },
	} = selection;
	return !nodeBefore;
};

export const getMarkBoundary = (from: number, to: number, mark: Mark, doc: ProseNode) => {
	let start = from;
	let end = to;

	while (true) {
		const $start = doc.resolve(start - 1);
		if ($start.nodeAfter && $start.nodeAfter.marks.includes(mark)) {
			start = $start.pos;
		} else {
			break;
		}
	}
	while (true) {
		const $end = doc.resolve(end + 1);
		if ($end.nodeBefore && $end.nodeBefore.marks.includes(mark)) {
			end = $end.pos;
		} else {
			break;
		}
	}
	if (mark.type.spec.inclusive) {
		end += 1;
	}
	return [start, end];
};

export const getBlockPos = (pos: number, doc: ProseNode, side = -1) => {
	// find the position that marks start/end of blocks (which are not inside any blocks)
	let start = 0;
	for (let i = 0; i < doc.childCount; i++) {
		const child = doc.child(i);
		if (start + child.nodeSize > pos || start === pos) {
			if (side <= 0) {
				return start;
			} else {
				return start + child.nodeSize;
			}
		}
		start = start + child.nodeSize;
	}
	return null;
};

export const expandSelectionToBlocks = (start: number, end: number, doc: ProseNode) => {
	// check if start/end is already at block edges.
	const $start = doc.resolve(start);
	const $end = doc.resolve(end);
	if ($start.node().type.name === "doc" && $end.node().type.name === "doc") {
		return [start, end];
	}
	return [$start.start(1) - 1, $end.end(1) + 1];
};

export function isPosWithinBounds(node: ProseNode, index: number, leafText = "\n"): boolean {
	return index <= getText(node, leafText).length;
}

export const getPosByTextIndex = (node: ProseNode, index: number, leafText = "\n"): number => {
	if (index === 0) return 0;
	if (!isPosWithinBounds(node, index, leafText)) {
		throw new Error(`text index(${index}) out of range.`);
	}
	let textLength = 0;
	let pos = 0;
	const children: ProseNode[] = [];
	for (let index = 0; index < node.childCount; index++) {
		children.push(node.child(index));
	}
	for (const child of children) {
		const prevTextLength = textLength;
		textLength += getText(child, leafText).length;
		if (textLength > index) {
			if (child.content.size) {
				return pos + 1 + getPosByTextIndex(child, index - prevTextLength, leafText);
			} else {
				return pos + getText(child, leafText).slice(0, index - prevTextLength).length;
			}
		}
		if (textLength === index) {
			return pos + child.nodeSize;
		} else {
			pos += child.nodeSize;
		}
	}
	return pos;
};

export const getStrPos = (doc: ProseNode, str: string, caseSensitive = true): [number, number][] => {
	const chunksIndexes: [number, number][] = [];
	if (!str.length) return [];
	let docText = getText(doc);
	let searchText = str;
	if (!caseSensitive) {
		docText = docText.toLowerCase();
		searchText = str.toLowerCase();
	}
	let pos = 0;
	docText.split(searchText).forEach(chunk => {
		const currStart = chunk.length + pos;
		if (currStart === docText.length) {
			return;
		}
		const currEnd = currStart + searchText.length;
		pos = currEnd;
		chunksIndexes.push([currStart, currEnd]);
	});
	return chunksIndexes.map(([start, end]) => [getPosByTextIndex(doc, start), getPosByTextIndex(doc, end)]);
};

export const decorateText = (doc: ProseNode, str: string, attrs: DecorationAttrs) => {
	const decos: Decoration[] = [];
	const strPos = getStrPos(doc, str);
	strPos.forEach(([start, end]) => {
		decos.push(Decoration.inline(start, end, attrs));
	});
	return decos;
};

export const markText = (doc: ProseNode, str: string, mark: Mark) => {
	const tr = new Transform(doc);
	getStrPos(doc, str, false).forEach(([start, end]) => {
		tr.addMark(tr.mapping.map(start), tr.mapping.map(end), mark);
	});
	return tr.doc;
};

export const blurView = (view: EditorView) => {
	if (view.dom instanceof HTMLElement) {
		view.dom.blur();
	}
};

/**
 * Check if a given range has a given mark or mark type across the entire
 * thing.
 */
export function entireRangeHasMark(
	from: number,
	to: number,
	mark: Mark | MarkType,
): (s: EditorState) => Mark | undefined {
	return function entireRangeHasMarkInner(state: EditorState) {
		let markInstance: Mark | undefined,
			hasMark = true;
		state.doc.slice(from, to).content.descendants((node, pos) => {
			const parent = state.doc.resolve(from + pos).parent;
			// As soon as we find a node that doesn't have the mark, we can stop searching.
			if (!hasMark) return false;
			// If we find a node that has the mark, we can determin the sub-range represented by the node to have the mark.
			// If the inner nodes of the current node doesn't have/allow the mark, we still consider the range to have the mark.
			let maybeMarkInstance = mark.isInSet(node.marks); // the truthy value of this could be the mark instance or boolean true
			if (maybeMarkInstance) {
				if (maybeMarkInstance === true) {
					// mark is a Mark instance
					markInstance = mark as Mark;
				} else {
					markInstance = maybeMarkInstance;
				}
				return false;
			}
			// If we find a node that can have the mark but doesn't, we can determin the range doesn't all have the mark.
			if (parent?.type.allowsMarkType(mark instanceof MarkType ? mark : mark.type)) {
				hasMark = false;
				return false;
			}
			return true;
		});
		return hasMark && markInstance;
	};
}

/**
 * Check if a mark type is active on the current selection.
 */
export function markActive($type: Mark | MarkType | null) {
	if (!$type) {
		throw Error(`Cannot call 'markActive' with an invalid mark type. Received '$type' as '${$type}'.`);
	}
	return function (state: EditorState): boolean {
		const { from, to, $from, empty } = state.selection;
		if (empty) {
			return !!$type.isInSet(state.storedMarks || $from.marks());
		} else {
			return !!entireRangeHasMark(from, to, $type)(state);
		}
	};
}

/**
 * Check if a given node type is active on the current selection.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function nodeActive(typeName: string, wantAttrs: { [k: string]: any } = {}) {
	return function (state: EditorState): boolean {
		const { $to, $from, to, from } = state.selection;
		if ($from.sameParent($to)) {
			for (let i = $from.depth; i >= 0; i -= 1) {
				const node = $from.node(i);
				if (node.type.name === typeName && isMatch(node.attrs, wantAttrs)) return true;
			}
		} else {
			const typesInSelection = new Map();
			state.doc.slice(from, to).content.descendants(node => {
				typesInSelection.set(node.type.name, node.attrs);
				return false;
			});
			if (typesInSelection.size !== 1 || !typesInSelection.has(typeName)) {
				return false;
			}
			const haveAttrs = typesInSelection.get(typeName);
			return isMatch(haveAttrs, wantAttrs);
		}
		return false;
	};
}

const blockGroupRegex = /\bblock\b/;

/**
 * Check if `node` is a Fellow block (in the sense of having a NoteBlock in the
 * database).
 */
export function isBlock(node: ProseNode): boolean {
	if (!node.type.spec.group) return false;
	return blockGroupRegex.test(node.type.spec.group);
}

/** A ProseMirror object that has content; a Node, Fragment, or Slice. */
type Content = ProseNode | Fragment | Slice;
/** A function for use with nodesBetween or descendants */
type BlockIteratee = (block: ProseNode, start: number, parent: ProseNode | null, index: number) => void | boolean;

function normalizeContent(content: Content): Fragment {
	if (content instanceof Fragment) return content;
	return content.content;
}

/**
 * Invoke `fn` for blocks between `from` and `to` in `content`. If `fn` returns
 * true or undefined, the current block will be descended into if it can have
 * block children.
 */
export function blockNodesBetween(content: Content, from: number, to: number, fn: BlockIteratee): void {
	normalizeContent(content).nodesBetween(from, to, function blockIteratee(node, start, parent, index) {
		if (isBlock(node)) {
			fn(node, start, parent, index);
		}
		return false;
	});
}

/**
 * Invoke `fn` for all blocks in `content`. If `fn` returns true or undefined,
 * the current block will be descended into if it can have block children.
 */
export function blockDescendants(content: Content, fn: BlockIteratee): void {
	const fragment = normalizeContent(content);
	blockNodesBetween(fragment, 0, fragment.size, fn);
}

function closestBlockAndDepth($pos: ResolvedPos): [depth: number, node: ProseNode] {
	for (let { depth } = $pos; depth >= 0; depth -= 1) {
		const node = $pos.node(depth);
		if (isBlock(node)) {
			return [depth, node];
		}
	}
	throw new Error("No block found above position");
}

/**
 * Find the depth of the closest parent node that is a block from the given
 * resolved position.
 *
 * @returns A depth number.
 */
export function closestBlockDepth($pos: ResolvedPos): number {
	const [depth, _node] = closestBlockAndDepth($pos);
	return depth;
}

/**
 * Find the innermost block that $pos is within.
 *
 * @returns A prose node that is a block (according to isBlock).
 */
export function closestBlock($pos: ResolvedPos): ProseNode {
	const [_depth, node] = closestBlockAndDepth($pos);
	return node;
}

/**
 * Get the position where a new block can be inserted after the given resolved
 * position.
 *
 * @param before Get the position before the block at $pos instead of after.
 */
export function nextBlockInsertionPos($pos: ResolvedPos, before = false): number {
	// If the position is directly inside the doc node, a block can be inserted
	// there.
	if ($pos.node().type.name === "doc") return $pos.pos;
	const blockDepth = closestBlockDepth($pos);
	if (blockDepth === undefined) {
		throw new Error("No block above $pos");
	}
	if (before) return $pos.start(blockDepth) - 1;
	else return $pos.end(blockDepth) + 1;
}

/**
 * Given a node, find a child block with a blockId matching the given
 * `blockId` and its position.
 */
export function findBlockAndPos(node: ProseNode, blockId: string): [block: ProseNode, pos: number] | undefined {
	for (let index = 0, pos = 0; index < node.childCount; index++) {
		const child = node.child(index);

		if (isBlock(child) && child.attrs.blockId === blockId) {
			return [child, pos];
		}

		pos += child.nodeSize;
	}

	return undefined;
}

/**
 * Checks if an event happened for elements that either them or their parents possess given tags or classes.
 * This is useful when you want to stop event propagations in nodes if
 * they happened on a specific part of the DOM.
 */
export function eventMatchTagsOrClasses(
	event: Event,
	invalidTags: Array<string>,
	invalidClasses: Array<string>,
): boolean {
	if (!(event.target instanceof Element)) return false;

	const element = event.target;
	const parent = element.parentElement;

	const conditions = {
		invalidElementName: invalidTags.includes(element.tagName.toLowerCase()),
		invalidElementClass:
			typeof element.className === "string" &&
			invalidClasses.some(invalidClass => element.className.includes(invalidClass)),
		invalidParentName: !!parent && invalidTags.includes(parent.tagName.toLowerCase()),
		invalidParentClass:
			!!parent &&
			typeof parent.className === "string" &&
			invalidClasses.some(invalidClass => parent.className.includes(invalidClass)),
	};

	return (
		conditions.invalidElementName ||
		conditions.invalidElementClass ||
		conditions.invalidParentName ||
		conditions.invalidParentClass
	);
}

export function isNodeEmpty(node: ProseNode): boolean {
	// To avoid a full traversal of this node for the text content, we'll just
	// cheat a bit and check certain things first.
	if (node.attrs.placeholder) return false;
	const firstChild = node.firstChild;
	if (!firstChild) return true;
	if (firstChild.isText && firstChild.text) return false;
	return !node.textContent;
}

/**
 * Determine whether a node is assignable (meaning can have one or more assignees)
 */
const assignableNodeType: readonly string[] = ["agenda", "todo", "goal"];
export function isNodeAssignable(node: ProseNode): boolean {
	const {
		type: { name },
	} = node;
	return assignableNodeType.includes(name);
}

/**
 * Determine whether a node has a decoration before it (checkbox, pseudo-element etc.)
 */
const nodeTypeWithPrefixDecor: readonly string[] = ["agenda", "todo", "goal", "bullet-point", "numbered-list-item"];
export function isNodeHasPrefixDecor(node: ProseNode): boolean {
	const {
		type: { name },
	} = node;
	return nodeTypeWithPrefixDecor.includes(name);
}
